import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { format } from "date-fns";
import {
  Award,
  Check,
  CheckCircle,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MessageCircle,
  Megaphone,
  MessageSquareReply,
  Minus,
  Pencil,
  Quote,
  Video,
  X,
  XCircle,
} from "lucide-react";
import { toast } from "sonner";
import {
  Button,
  Card,
  ContactCell,
  DropdownMenu,
  Input,
  PlayerCell,
  Skeleton,
  SplitView,
  Textarea,
  ButtonGroup,
} from "@/components/ui";
import { SocialSvg } from "@/components/SocialSvg";
import { HighlightedQuote } from "@/components/ShareDesignPost/quote/HighlightedQuote";
import { SocialNetworksId } from "@/interfaces";
import { Fan, FanServices, Form, Media, Review, ReviewReplyStatus, ReviewServices } from "@/services/bedrock";
import {
  ReviewSentimentBadge,
  ReplyStatus,
  ReviewRating,
  ReviewReplyItem,
  ReviewStatusBadge,
  getTopLevelReviewReplies,
  countReviewReplies,
  SOURCE_SYSTEM_CONFIG,
  getInternalBadgeName,
  getReviewSentiment,
  getSentimentStyles,
  REVIEW_SENTIMENT_CONFIG,
  FEATURED_SHAREABILITY_MIN_SCORE,
  NO_REPLY_NEEDED_LABEL,
  type ReviewSentimentFilter,
  ReviewReplyDialog,
} from "@/components/reviews";
import RecordVideoDialog from "@/containers/RecordVideo/RecordVideoDialog";
import { VideoPageModal } from "@/containers/VideoPages";
import { FanDialog } from "../../Fans/components";
import { FormDialog } from "../../Forms/components/FormDialog";
import { InfoTip } from "../../Overview/overviewUi";
import { cn } from "@/lib/utils";
import { ShareFlowHost } from "@/components/ShareDesignPost/ShareFlowHost";
import { useShareFlow } from "@/components/ShareDesignPost/ShareFlowProvider";
import { MiniPager } from "@/components/ShareDesignPost/MiniPager";
import { primaryPostabilityReason } from "@/components/ShareDesignPost/postability";
import { networkExceptions, PostReach, statusLine } from "@/components/ShareDesignPost/SharedOnSocialSection";
import { PostabilityReason } from "@/services/bedrock/dtos/Review";
import { ReviewPostSummary } from "@/services/bedrock/dtos/ReviewPost";
import { useAvailableDesigns } from "@/components/ShareDesignPost/hooks/useAvailableDesigns";
import { useReviewPosts } from "@/components/ShareDesignPost/hooks/useReviewPosts";
import { useDynamicPromptsAccess } from "@/utils/useDynamicPromptsAccess";
import { useCoachReviewsFilters } from "./CoachReviewsFiltersContext";

const CONDENSED_QUOTE_MAX_LENGTH = 180;

const SIDE_PANEL_INFO = {
  shareability:
    "Social Score reflects how effective this review would be if shared publicly. Scores of 90+ are marked Featured and prioritized in widgets and sorts.",
  condensedQuote: "A short excerpt for public display beside the Player's name and profile, such as in review widgets.",
  replyStatus: "Whether this review has been replied to, marked as not needing a reply, or still needs a response.",
  sentiment: "Overall tone of the review — positive, neutral, or negative. Used for reporting and triage.",
  approvedToShare: "Whether this review may be shared on social media or in public-facing widgets.",
  socialPosts: "Personalized posts made from this review for its Player, with how each one is doing.",
} as const;

function clampScore(value: number): number {
  if (!Number.isFinite(value)) return 0;
  return Math.min(100, Math.max(0, Math.round(value)));
}

type Rgb = readonly [number, number, number];

const SHAREABILITY_COLOR_STOPS: { at: number; color: Rgb }[] = [
  { at: 0, color: [239, 68, 68] }, // red-500
  { at: 0.5, color: [234, 179, 8] }, // yellow-500
  { at: 1, color: [34, 197, 94] }, // green-500
];

function shareabilityColor(score: number): string {
  const t = clampScore(score) / 100;
  const stops = SHAREABILITY_COLOR_STOPS;
  let i = 0;
  while (i < stops.length - 1 && t > stops[i + 1].at) i += 1;
  const from = stops[i];
  const to = stops[Math.min(i + 1, stops.length - 1)];
  const span = to.at - from.at || 1;
  const local = Math.min(1, Math.max(0, (t - from.at) / span));
  const r = Math.round(from.color[0] + (to.color[0] - from.color[0]) * local);
  const g = Math.round(from.color[1] + (to.color[1] - from.color[1]) * local);
  const b = Math.round(from.color[2] + (to.color[2] - from.color[2]) * local);
  return `rgb(${r}, ${g}, ${b})`;
}

function getReplyStatusOptions(isInternal: boolean): { value: ReviewReplyStatus; label: string }[] {
  return [
    { value: "replied", label: "Replied" },
    { value: "skipped", label: NO_REPLY_NEEDED_LABEL },
    { value: "unreplied", label: isInternal ? "Unreplied" : "Needs Reply" },
  ];
}

function getReplyStatusValue(review: Review): ReviewReplyStatus {
  if (review.replied) return "replied";
  if (review.replySkipped) return "skipped";
  return "unreplied";
}

interface ReviewDetailPanelProps {
  review: Review;
  onUpdateReplyStatus: (replyStatus: ReviewReplyStatus) => void;
  isUpdatingReplyStatus?: boolean;
  onPreviousReview?: () => void;
  onNextReview?: () => void;
  onClose?: () => void;
}

function SidePanel({
  label,
  children,
  className,
  action,
  info,
  labelAccessory,
}: {
  label: string;
  children: React.ReactNode;
  className?: string;
  action?: React.ReactNode;
  info?: string;
  labelAccessory?: React.ReactNode;
}) {
  return (
    <div className={cn("group rounded-lg border border-slate-200 bg-white px-3.5 py-3", className)}>
      <div className="mb-1.5 flex items-center gap-1.5 min-h-4">
        <div className="flex min-w-0 flex-1 items-center gap-1.5">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">{label}</div>
          {labelAccessory}
          {action}
        </div>
        {info ? <InfoTip text={info} /> : null}
      </div>
      <div className="text-sm text-slate-800 min-w-0">{children}</div>
    </div>
  );
}

const firstNameOf = (name: string | null | undefined) => name?.trim().split(/\s+/)[0] || null;

/** "unknown": the server says it can't be posted without a reason this app knows. */
type ShareBlocker = PostabilityReason | "unassigned" | "unknown";

// Why a review can't be posted, from the admin's seat, naming the setting to change where there is one.
const SHARE_BLOCKER_MESSAGES: Record<ShareBlocker, string> = {
  unassigned: "This review isn't linked to a player yet, so it can't be shared.",
  not_positive: "Only positive reviews can be shared.",
  not_approved: 'Sharing is turned off for this review. Change "Approved to share" to allow it.',
  inactive: "This review is inactive and can't be shared.",
  test: "Test reviews can't be shared.",
  analysis_pending: "We're still reading this review. Check back in a minute.",
  unknown: "This review can't be shared.",
};

function postabilityBlocker(review: Review): ShareBlocker | null {
  if (!review.player) return "unassigned";
  if (review.postable !== false) return null;
  const reason = primaryPostabilityReason(review.postabilityReasons);
  return reason && reason in SHARE_BLOCKER_MESSAGES ? reason : "unknown";
}

/** Whether this review can be posted for its player, what's been posted from it, and how to start a post. */
function useCoachShare(review: Review) {
  const { canPostForPlayers } = useDynamicPromptsAccess();
  const { open, openPost } = useShareFlow();
  const player = review.player;
  const reviewId = Number(review.id);
  const designs = useAvailableDesigns(canPostForPlayers && player ? player.id : null);
  const posts = useReviewPosts(canPostForPlayers ? reviewId : null);

  const hasReviewDesign = !!designs.data?.some(design => design.contextType === "review");
  const blocker = postabilityBlocker(review);

  return {
    enabled: canPostForPlayers,
    playerFirstName: firstNameOf(player?.fullName),
    loading: (!!player && designs.isLoading) || posts.isLoading,
    hasReviewDesign,
    posts: posts.data ?? [],
    blocker,
    canPost: canPostForPlayers && !!player && !blocker && hasReviewDesign,
    startShare: () => {
      if (!player) return;
      open({ kind: "coach", source: "review", playerId: player.id, playerUserId: player.userId, reviewId });
    },
    openPost: (postId: number, socialNetworkId: SocialNetworksId | null) =>
      openPost(postId, socialNetworkId ?? undefined),
  };
}

/** The action row's way to post this review for its player; absent whenever posting isn't possible. */
function PostForPlayerButton({ review }: { review: Review }) {
  const share = useCoachShare(review);
  if (!share.canPost) return null;
  return (
    <Button variant="outline" className="h-9 font-semibold text-xs gap-1.5" onClick={share.startShare}>
      <Megaphone className="size-3.5" />
      Post for {share.playerFirstName ?? "player"}
    </Button>
  );
}

function SocialPostSummary({
  post,
  onOpen,
}: {
  post: ReviewPostSummary;
  onOpen: (postId: number, socialNetworkId: SocialNetworksId | null) => void;
}) {
  const open = () => onOpen(post.postId, (post.primarySocialNetworkId as SocialNetworksId | null) ?? null);
  return (
    <div className="flex gap-2.5" data-testid="social-post">
      <button type="button" onClick={open} className="shrink-0 cursor-pointer" aria-label="Open post">
        {post.thumbnailUrl ? (
          <img src={post.thumbnailUrl} alt="" className="size-12 rounded-md border border-slate-200 object-cover" />
        ) : (
          <div className="size-12 rounded-md bg-slate-100" />
        )}
      </button>
      <div className="flex min-w-0 flex-col gap-0.5 text-xs text-slate-500">
        <button
          type="button"
          onClick={open}
          className="cursor-pointer text-left text-sm font-semibold text-slate-800 hover:underline"
        >
          {statusLine(post)}
        </button>
        <PostReach post={post} />
        {networkExceptions(post).map(exception => (
          <span key={exception}>{exception}</span>
        ))}
        {post.createdBy && <span>Posted by {post.createdBy.name}</span>}
      </div>
    </div>
  );
}

/** The right column's record of posts made from this review, or why it can't be posted. */
function SocialPostsPanel({ review }: { review: Review }) {
  const share = useCoachShare(review);
  const [index, setIndex] = useState(0);
  useEffect(() => setIndex(0), [review.id]);

  if (!share.enabled) return null;
  // Nothing to post with and nothing posted: the box would only be noise.
  if (!share.loading && !share.blocker && !share.hasReviewDesign && share.posts.length === 0) return null;

  const current = share.posts[Math.min(index, share.posts.length - 1)];
  return (
    <SidePanel
      label="Social posts"
      info={SIDE_PANEL_INFO.socialPosts}
      action={
        share.posts.length > 1 ? (
          <MiniPager
            index={index}
            total={share.posts.length}
            onChange={setIndex}
            className="ml-auto flex items-center gap-1"
          />
        ) : undefined
      }
    >
      <div className="flex flex-col gap-2" data-testid="social-posts-panel">
        {share.loading ? (
          <Skeleton className="h-4 w-32" />
        ) : (
          <>
            {current ? (
              <SocialPostSummary post={current} onOpen={share.openPost} />
            ) : (
              !share.blocker && <span className="text-slate-500">Not posted yet</span>
            )}
            {share.blocker && <p className="m-0 text-slate-500">{SHARE_BLOCKER_MESSAGES[share.blocker]}</p>}
            {current && share.canPost && (
              <Button
                variant="link"
                size="inline"
                className="self-start text-xs text-slate-500"
                onClick={share.startShare}
              >
                Post again
              </Button>
            )}
          </>
        )}
      </div>
    </SidePanel>
  );
}

export function ReviewDetailPanel({
  review,
  onUpdateReplyStatus,
  isUpdatingReplyStatus = false,
  onPreviousReview,
  onNextReview,
  onClose,
}: ReviewDetailPanelProps) {
  const queryClient = useQueryClient();
  const { filters } = useCoachReviewsFilters();
  const primaryRatingType = filters.ratingType;

  const [showReplyDialog, setShowReplyDialog] = useState(false);
  const [replyDialogMode, setReplyDialogMode] = useState<"video" | "text">("text");
  const [showRecordDialog, setShowRecordDialog] = useState(false);
  const [scriptToRecord, setScriptToRecord] = useState("");
  const [recordedMedia, setRecordedMedia] = useState<Media | null>(null);
  const [showVideoPageModal, setShowVideoPageModal] = useState(false);
  const [viewVideoPageId, setViewVideoPageId] = useState<number | null>(null);
  const [autoOpenShareModal, setAutoOpenShareModal] = useState(false);
  const [fanDialogOpen, setFanDialogOpen] = useState(false);
  const [selectedFan, setSelectedFan] = useState<Fan | null>(null);
  const [editingQuote, setEditingQuote] = useState(false);
  const [quoteDraft, setQuoteDraft] = useState("");
  const [editingScore, setEditingScore] = useState(false);
  const [scoreDraft, setScoreDraft] = useState(0);
  const [scoreInput, setScoreInput] = useState("0");
  const [showAllResponses, setShowAllResponses] = useState(false);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [selectedForm, setSelectedForm] = useState<Form | null>(null);

  const { data: fullReview, isLoading: isLoadingDetails } = useQuery({
    queryKey: ["review-details", review.id],
    queryFn: () =>
      ReviewServices.getReview(review.id, {
        extraFields: ["latest_content_page", "review_replies", "social_identity", "form"],
      }),
  });

  const activeReview = fullReview ?? review;
  const associatedForm = activeReview.form;
  const associatedFormName = associatedForm?.displayName;
  const isInternal = SOURCE_SYSTEM_CONFIG[activeReview.sourceSystem]?.isInternal ?? false;
  const sourceName = isInternal
    ? getInternalBadgeName(activeReview)
    : SOURCE_SYSTEM_CONFIG[activeReview.sourceSystem]?.name ?? activeReview.sourceSystem;
  const sentiment = getReviewSentiment(activeReview);
  const sentimentTheme = getSentimentStyles(sentiment);
  const committedScore = activeReview.aiShareabilityScore ?? null;
  const hasCommittedScore = committedScore != null;
  const isScoreDirty = editingScore && (!hasCommittedScore || scoreDraft !== committedScore);
  const displayScore = editingScore ? scoreDraft : committedScore;
  const isFeatured = displayScore != null && displayScore >= FEATURED_SHAREABILITY_MIN_SCORE;
  const publishedAt = activeReview.publishedAt ? new Date(activeReview.publishedAt) : null;
  const publishedLabel = publishedAt ? format(publishedAt, "MMM d, yyyy h:mm a") : undefined;
  const replyStatus = getReplyStatusValue(activeReview);

  const topLevelReplies = getTopLevelReviewReplies(activeReview.reviewReplies);
  const hasResponses = topLevelReplies.length > 0;
  const firstReplyHasNested = (topLevelReplies[0]?.reviewReplies?.length ?? 0) > 0;
  const canExpandResponses = topLevelReplies.length > 1 || firstReplyHasNested;
  const responseCount = countReviewReplies(topLevelReplies);
  const visibleReplies =
    hasResponses && (showAllResponses || !canExpandResponses)
      ? topLevelReplies
      : hasResponses
      ? topLevelReplies.slice(0, 1)
      : [];

  useEffect(() => {
    setEditingQuote(false);
    setQuoteDraft(activeReview.condensedQuote ?? "");
    setShowAllResponses(false);
  }, [activeReview.id, activeReview.condensedQuote]);

  useEffect(() => {
    const next = activeReview.aiShareabilityScore ?? null;
    setEditingScore(false);
    setScoreDraft(next ?? 0);
    setScoreInput(next != null ? String(next) : "");
  }, [activeReview.id, activeReview.aiShareabilityScore]);

  const { data: generatedTextData, isLoading: isGeneratingText } = useQuery({
    queryKey: ["reviews-text-generation", activeReview.id],
    queryFn: () => ReviewServices.generateResponseText(activeReview.id, {}),
    enabled: showReplyDialog && replyDialogMode === "text",
  });

  const { data: generatedScriptData, isLoading: isGeneratingScript } = useQuery({
    queryKey: ["reviews-script-generation", activeReview.id],
    queryFn: () => ReviewServices.generateResponseScript(activeReview.id, {}),
    enabled: showReplyDialog && replyDialogMode === "video",
  });

  const generatedText = generatedTextData?.response || "";
  const generatedScript = generatedScriptData?.script || "";

  const invalidateReviewQueries = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ["reviews"] });
    queryClient.invalidateQueries({ queryKey: ["review-snapshot"] });
    queryClient.invalidateQueries({ queryKey: ["review-details"] });
    queryClient.invalidateQueries({ queryKey: ["review", String(activeReview.id)] });
  }, [queryClient, activeReview.id]);

  const saveFanMutation = useMutation({
    mutationFn: async (formData: Record<string, unknown>) => {
      if (!selectedFan) throw new Error("No fan selected");
      return FanServices.updateFan(selectedFan.id, formData);
    },
    onSuccess: () => {
      invalidateReviewQueries();
      queryClient.invalidateQueries({ queryKey: ["fans"] });
      setFanDialogOpen(false);
      setSelectedFan(null);
      toast.success("Contact updated");
    },
    onError: () => {
      toast.error("Error updating contact. Please try again.");
    },
  });

  const updateFieldsMutation = useMutation({
    mutationFn: (request: {
      condensedQuote?: string | null;
      sentiment?: ReviewSentimentFilter;
      approvedForSocial?: boolean;
      aiShareabilityScore?: number;
    }) => ReviewServices.updateReview(activeReview.id, request),
    onSuccess: () => {
      invalidateReviewQueries();
    },
    onError: () => {
      toast.error("Failed to update review. Please try again.");
    },
  });

  const openFanDialog = useCallback((fan: Fan, player?: Fan["player"]) => {
    setSelectedFan({
      ...fan,
      player: fan.player ?? player,
    });
    setFanDialogOpen(true);
  }, []);

  const openAssociatedForm = useCallback(() => {
    if (!associatedForm) return;
    setSelectedForm(associatedForm);
    setFormDialogOpen(true);
  }, [associatedForm]);

  const openReplyDialog = (mode: "text" | "video") => {
    setReplyDialogMode(mode);
    setShowReplyDialog(true);
  };

  const handleContinueToTeleprompter = (script: string) => {
    setShowReplyDialog(false);
    setScriptToRecord(script);
    setShowRecordDialog(true);
  };

  const setScore = (next: number) => {
    const clamped = clampScore(next);
    setScoreDraft(clamped);
    setScoreInput(String(clamped));
  };

  const cancelScore = () => {
    setScoreDraft(committedScore ?? 0);
    setScoreInput(committedScore != null ? String(committedScore) : "");
    setEditingScore(false);
  };

  const saveScore = async () => {
    if (!isScoreDirty) {
      setEditingScore(false);
      return;
    }
    try {
      await updateFieldsMutation.mutateAsync({ aiShareabilityScore: scoreDraft });
      setEditingScore(false);
      toast.success("Social Score updated");
    } catch {}
  };

  const saveQuote = async () => {
    const next = quoteDraft.trim();
    const current = (activeReview.condensedQuote ?? "").trim();
    if (next === current) {
      setEditingQuote(false);
      return;
    }
    try {
      await updateFieldsMutation.mutateAsync({ condensedQuote: next || null });
      setEditingQuote(false);
      toast.success("Condensed quote updated");
    } catch {}
  };

  const saveSentiment = async (next: ReviewSentimentFilter) => {
    if (next === activeReview.sentiment) return;
    try {
      await updateFieldsMutation.mutateAsync({ sentiment: next });
      toast.success("Sentiment updated");
    } catch {}
  };

  const saveApprovedForSocial = async (next: boolean) => {
    if (next === activeReview.approvedForSocial) return;
    try {
      await updateFieldsMutation.mutateAsync({ approvedForSocial: next });
      toast.success("Approved to share updated");
    } catch {}
  };

  const videoPageModalInitialObject = useMemo(() => {
    if (recordedMedia) return { media: recordedMedia };
    if (viewVideoPageId) return { videoPageId: viewVideoPageId };
    return undefined;
  }, [recordedMedia, viewVideoPageId]);

  const shareAuthorized =
    activeReview.approvedForSocial == null
      ? { label: "—", Icon: Minus, tone: "subtle" as const }
      : activeReview.approvedForSocial
      ? { label: "Yes", Icon: CheckCircle, tone: "positive" as const }
      : { label: "No", Icon: XCircle, tone: "muted" as const };

  const reviewerName = activeReview.fan?.displayName || activeReview.displayName || "Anonymous";

  const handlePosted = () => {
    invalidateReviewQueries();
    const first = firstNameOf(activeReview.player?.fullName);
    toast.success(first ? `Post created for ${first}.` : "Post created.");
  };
  const withShareFlow = (content: React.ReactNode) => (
    <ShareFlowHost
      playerId={activeReview.player?.id ?? null}
      playerUserId={activeReview.player?.userId ?? null}
      onBehalfOf
      onPosted={handlePosted}
    >
      {content}
    </ShareFlowHost>
  );

  const reviewNav = (
    <div className="mb-3 flex items-center gap-2">
      {onClose ? (
        <Button
          variant="outline"
          size="sm"
          className="h-8 shrink-0 gap-1.5 border-slate-300 bg-white font-semibold text-slate-700 shadow-sm hover:border-slate-400 hover:bg-slate-100 hover:text-slate-900"
          onClick={onClose}
        >
          <X className="size-3.5" />
          Close
        </Button>
      ) : (
        <div className="h-8 w-[4.75rem] shrink-0" aria-hidden />
      )}

      <div className="flex-1" />

      <ButtonGroup>
        <Button
          variant="outline"
          size="icon"
          className="bg-white text-slate-600"
          disabled={!onPreviousReview}
          onClick={onPreviousReview}
          aria-label="Previous review"
        >
          <ChevronLeft className="size-4" />
        </Button>
        <Button
          variant="outline"
          size="icon"
          className="bg-white text-slate-600"
          disabled={!onNextReview}
          onClick={onNextReview}
          aria-label="Next review"
        >
          <ChevronRight className="size-4" />
        </Button>
      </ButtonGroup>
    </div>
  );

  if (isLoadingDetails && !fullReview) {
    return withShareFlow(
      <SplitView.Detail bodyClassName="p-0 gap-0">
        <div className="@container mx-auto w-full min-w-80 max-w-5xl p-4">
          {reviewNav}
          <Skeleton className="h-[28rem] w-full rounded-xl" />
        </div>
      </SplitView.Detail>
    );
  }

  return withShareFlow(
    <SplitView.Detail bodyClassName="p-0 gap-0">
      <div className="@container mx-auto w-full min-w-80 max-w-5xl p-4">
        {reviewNav}

        <Card className="font-sans gap-0 border border-slate-200 py-0 shadow-sm overflow-hidden">
          <div className="grid grid-cols-1 gap-4 border-b border-slate-200 px-5 py-4 @min-[28rem]:grid-cols-2 @min-[28rem]:gap-6">
            <ContactCell
              label="Reviewer"
              name={reviewerName}
              email={activeReview.fan?.email ?? activeReview.email}
              phoneNumber={activeReview.fan?.phoneNumber}
              showAvatar={true}
              avatarClassName={cn(sentimentTheme.avatarGradient, "text-white")}
              onClick={activeReview.fan ? () => openFanDialog(activeReview.fan!, activeReview.player) : undefined}
              className={
                !activeReview.displayName && !activeReview.fan?.displayName ? "italic text-muted-foreground" : undefined
              }
              size="xl"
            />

            {activeReview.player ? (
              <PlayerCell label="Player" player={activeReview.player} showAvatar size="xl" />
            ) : (
              <div className="min-w-0">
                <div className="mb-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Player</div>
                <span className="text-sm italic text-slate-400">Unassigned</span>
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 @min-[40rem]:grid-cols-[minmax(0,1.4fr)_minmax(14rem,0.75fr)]">
            <div className="flex min-w-0 flex-col gap-4 px-5 py-5 @min-[40rem]:border-r @min-[40rem]:border-slate-200">
              <div className="flex items-end justify-between gap-3">
                <div className="flex min-w-0 flex-1 flex-col gap-1 text-xs leading-none">
                  <div className="flex min-w-0 items-center gap-1.5">
                    {activeReview.link ? (
                      <a
                        href={activeReview.link}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex shrink-0 items-center gap-1.5 font-semibold text-slate-500 transition-colors hover:text-primary"
                        aria-label={`Open in ${sourceName}`}
                      >
                        <SocialSvg networkId={activeReview.sourceSystem as SocialNetworksId} variant="icon" size="xs" />
                        <span>{sourceName}</span>
                        <ExternalLink className="size-3 shrink-0 opacity-60" />
                      </a>
                    ) : (
                      <span className="inline-flex shrink-0 items-center gap-1.5 font-semibold text-slate-500">
                        <SocialSvg networkId={activeReview.sourceSystem as SocialNetworksId} variant="icon" size="xs" />
                        <span>{sourceName}</span>
                      </span>
                    )}
                    {associatedFormName && (
                      <>
                        <span className="shrink-0 text-slate-300" aria-hidden>
                          ·
                        </span>
                        <button
                          type="button"
                          onClick={openAssociatedForm}
                          className="min-w-0 truncate text-left cursor-pointer font-medium text-slate-400 transition-colors hover:text-primary hover:underline"
                          aria-label={`Open form ${associatedFormName}`}
                          title={associatedFormName}
                        >
                          {associatedFormName}
                        </button>
                      </>
                    )}
                  </div>
                  {publishedLabel && <span className="font-normal text-muted-foreground">{publishedLabel}</span>}
                </div>
                <ReviewRating
                  review={activeReview}
                  fallback={null}
                  primaryRatingType={primaryRatingType}
                  secondaryRatingLayout="stacked"
                  className="shrink-0 items-end"
                />
              </div>

              {activeReview.content ? (
                <div className="relative pl-1">
                  <Quote className="absolute -top-3 -left-3 size-10 text-slate-100 rotate-180" aria-hidden />
                  <p className="relative m-0 text-sm leading-relaxed whitespace-pre-wrap text-slate-800">
                    {activeReview.content}
                  </p>
                </div>
              ) : (
                <p className="m-0 text-sm italic text-muted-foreground">No written comment.</p>
              )}

              {hasResponses && (
                <div className="flex flex-col gap-2">
                  <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">Responses</div>
                  {visibleReplies.map((reply, idx) => (
                    <ReviewReplyItem
                      key={reply.guid ?? idx}
                      reply={reply}
                      showNestedReplies={showAllResponses || !canExpandResponses}
                    />
                  ))}
                  {canExpandResponses && (
                    <button
                      type="button"
                      onClick={() => setShowAllResponses(prev => !prev)}
                      className={cn(
                        "self-start inline-flex items-center gap-1 py-1 px-2 rounded-full",
                        "text-xs font-semibold text-primary hover:bg-primary/10 hover:text-primary"
                      )}
                    >
                      <MessageCircle className="size-3" />
                      {showAllResponses ? "Show less" : `View all ${responseCount} responses`}
                    </button>
                  )}
                </div>
              )}

              <div className="flex flex-wrap items-center gap-2">
                {!isInternal ? (
                  <Button
                    className="h-9 font-semibold text-xs gap-1.5"
                    onClick={() => openReplyDialog("text")}
                    disabled={isUpdatingReplyStatus}
                  >
                    <MessageSquareReply className="size-3.5" />
                    Reply
                  </Button>
                ) : (
                  <Button
                    variant="ghost"
                    className="h-9 font-semibold text-xs text-slate-500 gap-1.5"
                    onClick={() => openReplyDialog("video")}
                    disabled={isUpdatingReplyStatus}
                  >
                    <Video className="size-3.5" />
                    Reply with Video
                  </Button>
                )}

                {activeReview.latestContentPage && (
                  <Button
                    variant="ghost"
                    className="h-9 font-semibold text-xs text-slate-500 gap-1.5"
                    onClick={() => {
                      setViewVideoPageId(activeReview.latestContentPage!.id);
                      setAutoOpenShareModal(false);
                      setShowVideoPageModal(true);
                    }}
                  >
                    <ExternalLink className="size-3.5" />
                    View Video Response
                  </Button>
                )}

                <PostForPlayerButton review={activeReview} />
              </div>
            </div>

            <div className="flex flex-col gap-3 bg-slate-50/50 px-4 py-5">
              <SidePanel
                label="Condensed quote"
                info={SIDE_PANEL_INFO.condensedQuote}
                action={
                  !editingQuote ? (
                    <button
                      type="button"
                      className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity p-0.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                      aria-label="Edit condensed quote"
                      onClick={() => {
                        setQuoteDraft(activeReview.condensedQuote ?? "");
                        setEditingQuote(true);
                      }}
                    >
                      <Pencil className="size-3" />
                    </button>
                  ) : undefined
                }
              >
                {editingQuote ? (
                  <div className="space-y-2">
                    <Textarea
                      value={quoteDraft}
                      onChange={e => setQuoteDraft(e.target.value)}
                      rows={4}
                      className={cn(
                        "text-sm bg-white",
                        quoteDraft.length > CONDENSED_QUOTE_MAX_LENGTH &&
                          "border-amber-400 focus-visible:ring-amber-400/40"
                      )}
                      autoFocus
                    />
                    <div className="flex flex-row items-center justify-between gap-1.5">
                      <div className="flex flex-wrap items-center gap-1.5">
                        <Button
                          size="sm"
                          variant="outline"
                          className="h-7 gap-1 text-xs bg-white"
                          disabled={updateFieldsMutation.isPending}
                          onClick={saveQuote}
                        >
                          <Check className="size-3" />
                          Save
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="h-7 gap-1 text-xs text-slate-500"
                          disabled={updateFieldsMutation.isPending}
                          onClick={() => {
                            setQuoteDraft(activeReview.condensedQuote ?? "");
                            setEditingQuote(false);
                          }}
                        >
                          <X className="size-3" />
                          Cancel
                        </Button>
                      </div>
                      <span
                        className={cn(
                          "text-xs",
                          quoteDraft.length > CONDENSED_QUOTE_MAX_LENGTH
                            ? "font-medium text-amber-600"
                            : "text-muted-foreground"
                        )}
                      >
                        {quoteDraft.length}/{CONDENSED_QUOTE_MAX_LENGTH}
                        {quoteDraft.length > CONDENSED_QUOTE_MAX_LENGTH ? " — recommended max" : ""}
                      </span>
                    </div>
                  </div>
                ) : activeReview.condensedQuote ? (
                  <p className="m-0 leading-relaxed italic text-slate-800">
                    “
                    <HighlightedQuote
                      quote={activeReview.condensedQuote}
                      phrases={activeReview.condensedHighlightPhrases}
                    />
                    ”
                  </p>
                ) : (
                  <button
                    type="button"
                    className="text-left text-sm italic text-slate-400 hover:text-slate-500"
                    onClick={() => {
                      setQuoteDraft("");
                      setEditingQuote(true);
                    }}
                  >
                    No condensed quote — click to add
                  </button>
                )}
              </SidePanel>

              <SidePanel
                label="Social Score"
                info={SIDE_PANEL_INFO.shareability}
                labelAccessory={
                  isFeatured ? <Award className="size-3.5 shrink-0 text-amber-500" aria-label="Featured" /> : undefined
                }
                action={
                  !editingScore ? (
                    <button
                      type="button"
                      className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity p-0.5 text-slate-400 hover:text-slate-600 cursor-pointer"
                      aria-label="Edit Social Score"
                      onClick={() => {
                        setScoreDraft(committedScore ?? 0);
                        setScoreInput(committedScore != null ? String(committedScore) : "0");
                        setEditingScore(true);
                      }}
                    >
                      <Pencil className="size-3" />
                    </button>
                  ) : undefined
                }
              >
                {editingScore ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-3">
                      <input
                        type="range"
                        min={0}
                        max={100}
                        step={1}
                        value={scoreDraft}
                        onChange={e => setScore(Number(e.target.value))}
                        disabled={updateFieldsMutation.isPending}
                        style={
                          {
                            background: `linear-gradient(to right, ${shareabilityColor(
                              scoreDraft
                            )} 0%, ${shareabilityColor(
                              scoreDraft
                            )} ${scoreDraft}%, #e2e8f0 ${scoreDraft}%, #e2e8f0 100%)`,
                            "--shareability-thumb": shareabilityColor(scoreDraft),
                          } as React.CSSProperties
                        }
                        className={cn(
                          "h-2 w-full min-w-0 flex-1 cursor-pointer appearance-none rounded-full",
                          "disabled:cursor-not-allowed disabled:opacity-60",
                          "[&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full",
                          "[&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:size-4",
                          "[&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-[var(--shareability-thumb)]",
                          "[&::-webkit-slider-thumb]:shadow-sm [&::-webkit-slider-thumb]:border-2",
                          "[&::-webkit-slider-thumb]:border-white [&::-webkit-slider-thumb]:mt-[-4px]",
                          "[&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-moz-range-track]:bg-transparent",
                          "[&::-moz-range-thumb]:size-4 [&::-moz-range-thumb]:rounded-full",
                          "[&::-moz-range-thumb]:border-0 [&::-moz-range-thumb]:bg-[var(--shareability-thumb)]"
                        )}
                        aria-label="Social Score"
                      />
                      <Input
                        type="number"
                        min={0}
                        max={100}
                        inputMode="numeric"
                        value={scoreInput}
                        disabled={updateFieldsMutation.isPending}
                        autoFocus
                        onChange={e => {
                          const raw = e.target.value;
                          setScoreInput(raw);
                          if (raw === "") return;
                          const parsed = Number(raw);
                          if (!Number.isFinite(parsed)) return;
                          setScoreDraft(clampScore(parsed));
                        }}
                        onBlur={() => {
                          if (scoreInput === "") {
                            setScore(committedScore ?? 0);
                            return;
                          }
                          setScore(Number(scoreInput));
                        }}
                        className="h-8 w-14 shrink-0 bg-white px-1.5 text-center text-sm tabular-nums"
                        aria-label="Social Score value"
                      />
                    </div>
                    <div className="flex flex-wrap items-center gap-1.5">
                      <Button
                        size="sm"
                        variant="outline"
                        className="h-7 gap-1 text-xs bg-white"
                        disabled={updateFieldsMutation.isPending || !isScoreDirty}
                        onClick={saveScore}
                      >
                        <Check className="size-3" />
                        Save
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 gap-1 text-xs text-slate-500"
                        disabled={updateFieldsMutation.isPending}
                        onClick={cancelScore}
                      >
                        <X className="size-3" />
                        Cancel
                      </Button>
                    </div>
                  </div>
                ) : hasCommittedScore ? (
                  <div className="flex items-center gap-3">
                    <div
                      className="h-2 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-200"
                      role="meter"
                      aria-valuenow={committedScore}
                      aria-valuemin={0}
                      aria-valuemax={100}
                      aria-label="Social Score"
                    >
                      <div
                        className="h-full rounded-full transition-colors"
                        style={{
                          width: `${committedScore}%`,
                          backgroundColor: shareabilityColor(committedScore),
                        }}
                      />
                    </div>
                    <span className="w-8 shrink-0 text-right text-sm font-semibold tabular-nums text-slate-800">
                      {committedScore}
                    </span>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="text-left text-sm italic text-muted-foreground hover:text-slate-700 cursor-pointer"
                    onClick={() => {
                      setScoreDraft(0);
                      setScoreInput("0");
                      setEditingScore(true);
                    }}
                  >
                    No score — click to add
                  </button>
                )}
              </SidePanel>

              <SidePanel
                label="Sentiment"
                info={SIDE_PANEL_INFO.sentiment}
                action={
                  <DropdownMenu>
                    <DropdownMenu.Trigger
                      render={props => (
                        <button
                          {...props}
                          type="button"
                          className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity cursor-pointer p-0.5 text-slate-400 hover:text-slate-600 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                          aria-label="Edit sentiment"
                          disabled={updateFieldsMutation.isPending}
                        >
                          <Pencil className="size-3" />
                        </button>
                      )}
                    />
                    <DropdownMenu.Content align="end">
                      {(Object.keys(REVIEW_SENTIMENT_CONFIG) as ReviewSentimentFilter[]).map(value => (
                        <DropdownMenu.Item
                          key={value}
                          onClick={() => saveSentiment(value)}
                          className={cn(activeReview.sentiment === value && "font-semibold")}
                        >
                          {REVIEW_SENTIMENT_CONFIG[value].title}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu>
                }
              >
                <ReviewSentimentBadge sentiment={sentiment} />
              </SidePanel>

              <SidePanel
                label="Reply status"
                info={SIDE_PANEL_INFO.replyStatus}
                action={
                  <DropdownMenu>
                    <DropdownMenu.Trigger
                      render={props => (
                        <button
                          {...props}
                          type="button"
                          className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity cursor-pointer p-0.5 text-slate-400 hover:text-slate-600 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                          aria-label="Edit reply status"
                          disabled={isUpdatingReplyStatus}
                        >
                          <Pencil className="size-3" />
                        </button>
                      )}
                    />
                    <DropdownMenu.Content align="end">
                      {getReplyStatusOptions(isInternal).map(option => (
                        <DropdownMenu.Item
                          key={option.value}
                          onClick={() => {
                            if (option.value === replyStatus) return;
                            onUpdateReplyStatus(option.value);
                          }}
                          className={cn(replyStatus === option.value && "font-semibold")}
                        >
                          {option.label}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu>
                }
              >
                <ReplyStatus review={activeReview} resolveInternalStatus />
              </SidePanel>

              <SidePanel
                label="Approved to share"
                info={SIDE_PANEL_INFO.approvedToShare}
                action={
                  <DropdownMenu>
                    <DropdownMenu.Trigger
                      render={props => (
                        <button
                          {...props}
                          type="button"
                          className="opacity-0 group-hover:opacity-100 focus-visible:opacity-100 transition-opacity cursor-pointer p-0.5 text-slate-400 hover:text-slate-600 rounded-md outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:opacity-50"
                          aria-label="Edit approved to share"
                          disabled={updateFieldsMutation.isPending}
                        >
                          <Pencil className="size-3" />
                        </button>
                      )}
                    />
                    <DropdownMenu.Content align="end">
                      {(
                        [
                          { value: true, label: "Yes", Icon: CheckCircle },
                          { value: false, label: "No", Icon: XCircle },
                        ] as const
                      ).map(option => (
                        <DropdownMenu.Item
                          key={String(option.value)}
                          onClick={() => saveApprovedForSocial(option.value)}
                          className={cn("gap-2", activeReview.approvedForSocial === option.value && "font-semibold")}
                        >
                          <option.Icon className="size-3.5" />
                          {option.label}
                        </DropdownMenu.Item>
                      ))}
                    </DropdownMenu.Content>
                  </DropdownMenu>
                }
              >
                <ReviewStatusBadge
                  label={shareAuthorized.label}
                  icon={shareAuthorized.Icon}
                  tone={shareAuthorized.tone}
                />
              </SidePanel>

              <SocialPostsPanel review={activeReview} />
            </div>
          </div>
        </Card>
      </div>

      {showReplyDialog && (
        <ReviewReplyDialog
          open={showReplyDialog}
          onOpenChange={setShowReplyDialog}
          review={activeReview}
          generatedText={replyDialogMode === "video" ? generatedScript : generatedText}
          isGenerating={replyDialogMode === "video" ? isGeneratingScript : isGeneratingText}
          mode={replyDialogMode}
          onContinueToTeleprompter={handleContinueToTeleprompter}
          onReplyWithVideo={!isInternal ? () => setReplyDialogMode("video") : undefined}
          onReplyWithText={!isInternal ? () => setReplyDialogMode("text") : undefined}
          onTextReplyComplete={() => {
            invalidateReviewQueries();
          }}
        />
      )}

      {showRecordDialog && (
        <RecordVideoDialog
          open={showRecordDialog}
          initialScript={scriptToRecord}
          initialRecordVideoType="teleprompter"
          onOpenChange={setShowRecordDialog}
          onDone={(media: Media) => {
            setShowRecordDialog(false);
            setRecordedMedia(media);
            setAutoOpenShareModal(true);
            setShowVideoPageModal(true);
          }}
        />
      )}

      {showVideoPageModal && videoPageModalInitialObject && (
        <VideoPageModal
          open={showVideoPageModal}
          initialObject={videoPageModalInitialObject}
          reviewId={activeReview.id}
          autoOpenShare={autoOpenShareModal}
          onClose={() => {
            setShowVideoPageModal(false);
            setRecordedMedia(null);
            setViewVideoPageId(null);
            setAutoOpenShareModal(false);
            invalidateReviewQueries();
          }}
        />
      )}

      <FanDialog
        open={fanDialogOpen}
        onOpenChange={open => {
          setFanDialogOpen(open);
          if (!open) setSelectedFan(null);
        }}
        selectedFan={selectedFan}
        onFormSubmit={formData => saveFanMutation.mutate(formData)}
        isSubmitting={saveFanMutation.isPending}
      />

      <FormDialog
        open={formDialogOpen}
        onOpenChange={open => {
          setFormDialogOpen(open);
          if (!open) setSelectedForm(null);
        }}
        form={selectedForm}
      />
    </SplitView.Detail>
  );
}
